import { MapPin, Phone, Clock } from 'lucide-react';
import { BUSINESS_INFO } from '../data';
import SectionHeader from './SectionHeader';
import useScrollReveal from '../hooks/useScrollReveal';
import { useLang } from '../context/LangContext';

export default function Location() {
  const leftRef  = useScrollReveal('reveal-left');
  const rightRef = useScrollReveal('reveal-right');
  const { t } = useLang();

  const contactItems = [
    {
      icon: <MapPin size={18} />,
      label: t.address,
      value: `${BUSINESS_INFO.address}, ${BUSINESS_INFO.city}`,
      href: `https://maps.google.com/?q=${encodeURIComponent(BUSINESS_INFO.address + ', ' + BUSINESS_INFO.city)}`,
    },
    {
      icon: <Phone size={18} />,
      label: t.phone,
      value: BUSINESS_INFO.phone,
      href: `tel:${BUSINESS_INFO.phoneRaw}`,
    },
    {
      icon: <span style={{ fontSize: '1rem' }}>💬</span>,
      label: 'WhatsApp',
      value: BUSINESS_INFO.whatsapp,
      href: `https://wa.me/${BUSINESS_INFO.whatsappRaw}`,
    },
    {
      icon: <Clock size={18} />,
      label: t.hours,
      value: 'Mon–Sat · 09:30 – 21:00',
      note: '(closed sporadically 14:00–15:30)',
    },
  ];

  return (
    <section id="visit" className="section-padding container">
      <SectionHeader
        tag={t.findUs}
        title={<>{t.visitTitle} <em style={{ fontStyle: 'italic', fontWeight: 300 }}>{t.visitTitleEm}</em></>}
        desc={t.visitDesc}
        id="visit-heading"
      />

      <div className="grid-2-col" style={{ marginTop: '3.5rem', alignItems: 'stretch', gap: '2rem' }}>
        {/* Map */}
        <div
          ref={leftRef}
          className="reveal-left"
          style={{
            overflow: 'hidden',
            borderRadius: '20px',
            border: '1px solid var(--border)',
            minHeight: '380px',
            position: 'relative',
          }}
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2801.22!2d12.2387!3d45.4903!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x477eb1d7d4c3f4f7%3A0x1234567890abcdef!2sVia%20Cappuccina%2015%2Fc%2C%2030172%20Mestre%20VE!5e0!3m2!1sen!2sit!4v1700000000000"
            width="100%"
            height="100%"
            style={{ border: 0, display: 'block', minHeight: '380px' }}
            allowFullScreen=""
            loading="lazy"
            title="Isha Boutique Location"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        {/* Info card */}
        <div
          ref={rightRef}
          className="reveal-right glass-card"
          style={{
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            gap: '2rem', padding: 'clamp(1.5rem,4vw,2.5rem)',
          }}
        >
          {contactItems.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>
              {/* Icon circle */}
              <div style={{
                width: '44px', height: '44px', flexShrink: 0,
                background: 'linear-gradient(135deg, rgba(183,110,121,0.12), rgba(232,180,184,0.06))',
                border: '1px solid rgba(183,110,121,0.15)',
                borderRadius: '12px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--accent)',
              }}>
                {item.icon}
              </div>

              <div>
                <div className="heading-sub" style={{ marginBottom: '0.25rem', fontSize: '0.62rem' }}>
                  {item.label}
                </div>
                {item.href ? (
                  <a
                    href={item.href}
                    target={item.href.startsWith('http') ? '_blank' : undefined}
                    rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    style={{
                      color: 'var(--accent)', fontWeight: 600, fontSize: '0.95rem',
                      transition: 'opacity 0.2s',
                    }}
                    onMouseOver={e => e.target.style.opacity = '0.75'}
                    onMouseOut={e  => e.target.style.opacity = '1'}
                  >
                    {item.value}
                  </a>
                ) : (
                  <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: '0.95rem' }}>
                    {item.value}
                    {item.note && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 400, marginTop: '0.2rem' }}>
                        {item.note}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
